import React, { useMemo, useState } from 'react';
import { Alert, Button as BsButton, Modal } from 'react-bootstrap';

import {
  GetClientGroupDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ClientSearch } from '../Clients/ClientSearch';
import { useClientContactsModal } from '../Contacts/useClientContactsModal';

import { ClientSimpleSearch } from './ClientSimpleSearch';

interface ClientGroupDetailsProps {
  group: GetClientGroupDto;
  onRefresh: () => void;
}

export const ClientGroupDetails: React.FC<ClientGroupDetailsProps> = props => {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('clientLng');
  const { open, modal } = useClientContactsModal(props.onRefresh);

  const activeItems = props.group.items ?? [];

  const handleRowClick = async (itemId: string) => {
    try {
      await pok.clientGroups.removeItem(itemId);
      notifications.saveCompleted();
      props.onRefresh();
    } catch (err) {
      notifications.caughtError(err as ResponseError);
    }
  };

  const handlePickerRowClick = (clientId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId],
    );
  };

  const handleAddClients = async () => {
    if (selectedClientIds.length === 0) {
      return;
    }
    setIsAdding(true);
    try {
      await pok.clientGroups.addClients(props.group.id, selectedClientIds);
      setSelectedClientIds([]);
      setShowPicker(false);
      notifications.saveCompleted();
      props.onRefresh();
    } catch (err) {
      notifications.caughtError(err as ResponseError);
    } finally {
      setIsAdding(false);
    }
  };

  const getClientsData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.clients.getAllByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.clients, t, tk],
  );

  return (
    <>
      <ClientSimpleSearch
        items={activeItems}
        onDeleteClick={handleRowClick}
        onRowClick={open}
      />
      {modal}
      <div className="d-flex justify-content-end pt-3">
        <BsButton variant="outline-primary" onClick={() => setShowPicker(true)}>
          {t(tk.clientGroup.addClients)}
        </BsButton>
      </div>

      <Modal
        show={showPicker}
        onHide={() => setShowPicker(false)}
        dialogClassName="modal-90w"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t(tk.clientGroup.selectClients)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="primary" className="mb-3">
            {t(tk.clientGroup.addClientsHint)}
          </Alert>
          <ClientSearch
            getData={getClientsData}
            getCount={pok.clients.getAllCount}
            onRowClick={handlePickerRowClick}
            enableMultiSelect={true}
            selectedKeys={selectedClientIds}
          />
        </Modal.Body>
        <Modal.Footer>
          <BsButton
            variant="outline-secondary"
            onClick={() => setShowPicker(false)}
          >
            {t(tk.button.cancel)}
          </BsButton>
          <BsButton
            variant="outline-primary"
            onClick={handleAddClients}
            disabled={isAdding || selectedClientIds.length === 0}
          >
            {t(tk.clientGroup.addClients)}
          </BsButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};
